// ./src/hooks/useFeatureUsage.ts
//
// DOES THIS ACCOUNT HAVE ANYTHING BEHIND A LOCKED PAGE? (ROADMAP A2 item 1, owner ruling 2026-09-25)
//
// ⚠⚠ FETCHED LAZILY, AND THAT IS THE WHOLE DESIGN. Almost nobody is ever locked out of anything: a
// paying host never asks this question, and pre-launch nobody can lapse at all. Loading it at boot
// would tax every session, on every sign-in, for a case that does not arise — so the request is made
// the first time a lock would actually render, and cached for the rest of the session.
//
// ⚠ ONE FETCH PER ACCOUNT, module-level. Several locked routes can mount in one navigation (a page
// and its drawers), and each asking the server the same question is three round trips for one answer.
// ⚠ Keyed by account: signing into a different account must not inherit the previous one's answer —
// that would unlock a module for somebody who never built anything.
//
// ⚠⚠ "UNKNOWN" IS NOT "NO". A caller must render nothing while the answer is in flight, never the
// lock — a paying host whose page is momentarily unresolved would otherwise see a flash of an upgrade
// screen, which is exactly the flicker `withPlanGate` already guards against with `isInitializing`.

import { useCallback, useEffect, useState } from "react";
import { OP_RESULT_CODES } from "@sdflc/api-helpers";

import { FeatureUsageService } from "@/utils/api";

type UsageState = "unknown" | "loading" | "ready";

interface Cache {
  accountId: string | null;
  state: UsageState;
  codes: Set<string>;
}

/** Module-level so every locked route in one navigation shares a single request. */
const cache: Cache = { accountId: null, state: "unknown", codes: new Set() };
const listeners = new Set<() => void>();

const notify = (): void => {
  listeners.forEach((fn) => fn());
};

const load = async (accountId: string): Promise<void> => {
  cache.accountId = accountId;
  cache.state = "loading";
  cache.codes = new Set();
  notify();

  const result = await FeatureUsageService.mine();

  /**
   * ⚠ A FAILED READ STAYS "ready" WITH NOTHING IN IT, deliberately. The caller's fallback is the lock
   * we already ship, so a host sees an upgrade screen — the same thing they saw before this existed.
   * Retrying forever would hammer the server on every locked route; failing OPEN would hand a paid
   * module to a free account on a network blip.
   */
  if (result?.code === OP_RESULT_CODES.OK && Array.isArray(result.data)) {
    // ⚠ Plain strings: the API deliberately declares no object type for this payload, because
    // `AppFeatureUsage` is a name ms_auth already exports and a duplicate type name stops the whole
    // supergraph composing. See the API's `featureUsageTypes.ts`.
    cache.codes = new Set(
      (result.data as Array<string | null>).map((code) => String(code ?? "").trim()).filter(Boolean),
    );
  }

  cache.state = "ready";
  notify();
};

export interface UseFeatureUsage {
  /** `true` only once the answer is in — see the note on "unknown is not no". */
  resolved: boolean;
  /** Whether the account has at least one live row behind this feature. */
  hasData: (featureCode: string) => boolean;
  /** Ask the server, at most once per account per session. Safe to call on every render. */
  ensureLoaded: () => void;
}

export const useFeatureUsage = (accountId: string | null | undefined): UseFeatureUsage => {
  const [, setTick] = useState(0);

  useEffect(() => {
    const listener = () => setTick((n) => n + 1);

    listeners.add(listener);

    return () => {
      listeners.delete(listener);
    };
  }, []);

  const ensureLoaded = useCallback(() => {
    const id = accountId ? String(accountId) : null;

    if (!id) {
      return;
    }

    // A different account, or a first ask: go and find out.
    if (cache.accountId !== id || cache.state === "unknown") {
      void load(id);
    }
  }, [accountId]);

  const hasData = useCallback(
    (featureCode: string): boolean => cache.state === "ready" && cache.codes.has(featureCode),
    // ⚠ `cache` is module state, so this must re-create on every notify — the tick above is what does
    // it. Listing it as a dependency would do nothing; the subscription is the mechanism.
    [],
  );

  return {
    resolved: cache.accountId === (accountId ? String(accountId) : null) && cache.state === "ready",
    hasData,
    ensureLoaded,
  };
};

/** ⚠ Test/guard seam: forget everything, so a check can drive the states in order. */
export const __resetFeatureUsageCache = (): void => {
  cache.accountId = null;
  cache.state = "unknown";
  cache.codes = new Set();
};
